import type { Market } from '@backmarket/http-api/src/api-models/Market'
import { getBrowserCookie } from '@backmarket/utils/cookies/getBrowserCookie'

import { COOKIES } from '../constants'
import type { DataLayerEvent, GoogleTagManagerConfig } from '../types'

const buffer: DataLayerEvent[] = []
let isCollectingEvents = false

function pushDataLayerEvent(event: DataLayerEvent) {
  // Google Tag Manager mutates the event provided to add a parameter: gtm.uniqueEventId
  window.dataLayer.push(event)
}

function isEcommerceEvent(event: DataLayerEvent) {
  return Object.hasOwn(event, 'ecommerce') && event.ecommerce !== null
}

/**
 * Reset ecommerce before each ecommerce event so GTM does not retain fields from the previous one.
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */
function createEcommerceResetEvent(): DataLayerEvent {
  return { ecommerce: null }
}

function getDataLayerEvents(event: DataLayerEvent) {
  if (!isEcommerceEvent(event)) return [event]

  return [createEcommerceResetEvent(), event]
}

export function pushEventToDataLayer(event: DataLayerEvent) {
  if (!import.meta.client) return

  const events = getDataLayerEvents(event)

  if (window.dataLayer) {
    events.forEach((eventToPush) => {
      if (isCollectingEvents) {
        pushDataLayerEvent(eventToPush)
      } else {
        buffer.push(eventToPush)
      }
    })
  }
}

/**
 *
 * @param reason - The gdpr event that triggered the start of the collection
 */
export function startCollectingEvents(reason?: DataLayerEvent) {
  isCollectingEvents = true

  if (reason) {
    pushEventToDataLayer(reason)
  }

  while (buffer.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    pushDataLayerEvent(buffer.shift()!)
  }
}

/**
 * For testing purposes. Resets to not collecting events
 */
export function stopCollectingEvents() {
  isCollectingEvents = false
}

export function getStringifiedConfig(config: GoogleTagManagerConfig) {
  return `auth=${config.auth}&preview=${config.preview}&id=${config.id}`
}

export function getServerSideStringifiedConfig(config: GoogleTagManagerConfig) {
  return `awl=${config.id.replace(/^GTM-/, '')}`
}

export function getGoogleTagManagerScript(
  config: GoogleTagManagerConfig,
  market: Market,
) {
  const stringifiedConfig = getServerSideStringifiedConfig(config)

  // The `noscript` portion still uses the former signature
  const noscriptStringifiedConfig = getStringifiedConfig(config)

  return `
    <!-- Google Tag Manager -->
    <script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://gobbledygook${market.domain}/uroaeaywmh99j9n.js?'+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${stringifiedConfig}');</script>
    <!-- End Google Tag Manager -->
    <!-- Google Tag Manager (noscript) -->
    <noscript><iframe src="https://gobbledygook${market.domain}/ns.html?${noscriptStringifiedConfig}"
    height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>
    <!-- End Google Tag Manager (noscript) -->
    <script>
      window.dataLayer = window.dataLayer || []
      // Some pushes to dataLayer store the HTML element that triggered it.
      // By doing so, it creates a reference to existing HTML in the Javascript.
      // Browser when garbage collecting the memory, looks for the referenced HTML in the Javascript
      // and keeps all the referenced HTML in the memory.
      // The more pushes to dataLayer are made, the more you will store HTML in memory that will never be cleared.
      // To prevent this all the HTML elements pushed to the dataLayer has to be cloned before so it's not the same reference.
      // For more informations: https://www.voorhoede.nl/en/blog/your-website-probably-has-a-memory-leak/
      window.dataLayer.push = function(event) {
        if (event['gtm.element']) {
          event['gtm.element'] = event['gtm.element'].cloneNode(true)
        }
        return Array.prototype.push.apply(this, arguments)
      }
    </script>`
}

type InitGTMParameters = {
  isOptInCookieNeeded: boolean
}

export function initGoogleTagManager({
  isOptInCookieNeeded,
}: InitGTMParameters) {
  const optIn = isOptInCookieNeeded
    ? Object.values(COOKIES).every((cookie) =>
        Boolean(getBrowserCookie(cookie)),
      )
    : true

  if (optIn) {
    startCollectingEvents()
  }
}
